import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({
  title,
  description = 'Journey through the fascinating stories of Indian history. From ancient civilizations to modern times, discover the rich cultural heritage of India.',
  image = '/images/hero-bg.jpg',
  type = 'website',
  publishedTime,
  modifiedTime,
  author = 'Namaskar India',
  keywords = 'Indian History Podcast, Hindu Mythology Podcast, Indian Culture Podcast, Epic Tales of India, Ancient India Stories, Indian Heritage Podcast, Historical and Mythological Stories, Indian Legends, Indian Cultural Heritage, Hindu Stories, Historical Narratives India',
  episodeData,
  canonicalUrl,
  language = 'en',
  noindex = false
}) => {
  const siteUrl = 'https://namaskarindia.live';
  const fullTitle = `${title} | Namaskar India`;
  const fullUrl = canonicalUrl ? `${siteUrl}${canonicalUrl}` : siteUrl;
  const imageUrl = image.startsWith('http') ? image : `${siteUrl}${image}`;

  // Base schema
  const baseSchema = {
    '@context': 'https://schema.org',
    '@type': type === 'article' ? 'Article' : 'WebSite',
    url: fullUrl,
    name: title,
    description,
    image: imageUrl,
    inLanguage: language,
    author: {
      '@type': 'Person',
      name: author
    },
    publisher: {
      '@type': 'Organization',
      name: 'Namaskar India',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/logo.png`
      }
    },
    ...(publishedTime && { datePublished: publishedTime }),
    ...(modifiedTime && { dateModified: modifiedTime })
  };

  // Podcast episode schema
  const episodeSchema = episodeData ? {
    '@context': 'https://schema.org',
    '@type': 'PodcastEpisode',
    name: episodeData.title,
    description: episodeData.description,
    datePublished: episodeData.publishDate,
    duration: episodeData.duration,
    url: episodeData.url,
    inLanguage: language,
    partOfSeries: {
      '@type': 'PodcastSeries',
      name: 'Namaskar India',
      url: siteUrl,
      inLanguage: language,
      description: 'A captivating podcast exploring Indian history and mythology. Journey through ancient tales, cultural heritage, and fascinating stories from India\'s past.'
    },
    audio: {
      '@type': 'AudioObject',
      contentUrl: episodeData.audioUrl,
      duration: episodeData.duration,
      encodingFormat: 'audio/mpeg',
      inLanguage: language
    },
    image: imageUrl
  } : null;

  // BreadcrumbList schema
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: title,
        item: fullUrl
      }
    ]
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="language" content={language} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Namaskar India" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {/* Article Meta */}
      {type === 'article' && publishedTime && (
        <>
          <meta property="article:published_time" content={publishedTime} />
          {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
          <meta property="article:author" content={author} />
          <meta property="article:section" content="Indian History" />
          <meta property="article:tag" content="Hindu Mythology" />
        </>
      )}

      {/* Language and Region */}
      <link rel="alternate" href={fullUrl} hreflang="en-US" />
      <link rel="alternate" href={fullUrl} hreflang="x-default" />

      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={fullUrl} />}

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(baseSchema)}
      </script>

      {episodeSchema && (
        <script type="application/ld+json">
          {JSON.stringify(episodeSchema)}
        </script>
      )}

      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
    </Helmet>
  );
};

export default SEO;
